import { Suspense, lazy, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Flex,
  Spinner,
  cn,
} from '@kognitos/lattice';

import type { Activity } from '@/modules/process-catalog/catalog';
import { CatalogBanner } from '@/modules/process-catalog/components/CatalogBanner';

// Lazy-load the catalog browser so the bundled taxonomy (catalog.json, ~112 KB)
// is only fetched when the user opens the modal. The ~1.5 MB template bodies are
// NOT in this chunk — `catalog.ts` reaches them through a dynamic import at
// instantiation time (see process-catalog/templates.ts).

/** Named so the click path and the hover prefetch share one cached import. */
const importProcessCatalogBrowser = () =>
  import('@/modules/process-catalog/components/ProcessCatalogBrowser');

const ProcessCatalogBrowser = lazy(() =>
  importProcessCatalogBrowser().then(m => ({
    default: m.ProcessCatalogBrowser,
  })),
);

/**
 * Props for the GetStartedOptions component.
 */
export interface IGetStartedOptionsProps {
  /** Called when the user picks an L3 activity from the process catalog. */
  onSelectActivity: (activity: Activity) => void;
  /** Disables selection while a preview draft is being created. */
  isCreatingPreview?: boolean;
  /** Id of the activity currently being turned into a preview, or null. */
  creatingActivityId?: string | null;
  /** Optional class name for the root container. */
  className?: string;
}

/**
 * Entry point into the finance process catalog: a banner of workstream folders
 * that opens the catalog in a large modal. Picking an L3 activity materializes
 * an editable SOP preview from its golden template. `CatalogBanner` summarizes
 * the taxonomy from `banner-preview.ts`, not `catalog.ts`, to keep it out of the
 * eager draft chunk; the modal holds no state, so every open starts fresh.
 *
 * @param {IGetStartedOptionsProps} props - Component props
 * @returns {JSX.Element} The catalog banner and its modal
 */
export function GetStartedOptions({
  onSelectActivity,
  isCreatingPreview = false,
  creatingActivityId = null,
  className,
}: IGetStartedOptionsProps) {
  const { t } = useTranslation('drafts');

  const [isOpen, setIsOpen] = useState(false);

  // Which L1 the modal should open drilled into, when the click came from a
  // folder rather than the banner's chrome.
  const [initialWorkstream, setInitialWorkstream] = useState<
    string | undefined
  >(undefined);

  const openCatalog = useCallback((workstream?: string) => {
    setInitialWorkstream(workstream);
    setIsOpen(true);
  }, []);
  const bannerRef = useRef<HTMLDivElement>(null);

  // Warm the catalog chunk on hover/focus so the click has nothing left to
  // wait for. Not on mount: that spends the 112 KB on every /drafts visit.
  const prefetchCatalog = useCallback(() => {
    // A failed prefetch must stay silent — `lazy` retries on click and the
    // Suspense boundary owns the real failure path.
    void importProcessCatalogBrowser().catch(() => {});
  }, []);

  // The modal stays open across the hand-off: creation is async and the row's
  // CTA owns the pending state. On success the navigation to the new draft
  // unmounts this page and takes the modal with it; on failure it stays open
  // over the error toast so the user can pick a different process.
  const handleSelectActivity = useCallback(
    (activity: Activity) => {
      onSelectActivity(activity);
    },
    [onSelectActivity],
  );

  return (
    <Flex width="full" justify="start" className={cn(className)}>
      <Dialog
        open={isOpen}
        onOpenChange={open => {
          setIsOpen(open);
          if (!open) setInitialWorkstream(undefined);
        }}
      >
        <CatalogBanner
          ref={bannerRef}
          onOpen={openCatalog}
          onPrefetch={prefetchCatalog}
        />

        <DialogContent
          className="flex h-[85vh] max-h-[85vh] max-w-[min(48rem,calc(100%-2rem))] flex-col gap-0 overflow-hidden p-0 sm:max-w-[min(48rem,calc(100%-2rem))]"
          data-testid="browse-catalog-dialog"
          onCloseAutoFocus={event => {
            event.preventDefault();
            bannerRef.current?.focus();
          }}
          // Focus the dialog itself on open
          onOpenAutoFocus={event => {
            event.preventDefault();
            const content = event.currentTarget;
            if (content instanceof HTMLElement) content.focus();
          }}
        >
          <DialogHeader className="p-4 pb-0">
            <DialogTitle>{t('drafts.processCatalog.header')}</DialogTitle>
            <DialogDescription className="sr-only">
              {t('drafts.processCatalog.taxonomy.hint')}
            </DialogDescription>
          </DialogHeader>

          <Flex
            direction="column"
            align="stretch"
            flex="1"
            overflow="hidden"
            padding={4}
            className="min-h-0"
          >
            {isOpen && (
              <Suspense
                fallback={
                  <Flex
                    align="center"
                    justify="center"
                    width="full"
                    flex="1"
                    className="min-h-0"
                    data-testid="browse-catalog-loading"
                  >
                    <Spinner size="sm" />
                  </Flex>
                }
              >
                <ProcessCatalogBrowser
                  onSelectActivity={handleSelectActivity}
                  initialWorkstream={initialWorkstream}
                  isCreating={isCreatingPreview}
                  creatingActivityId={creatingActivityId}
                  className="min-h-0 flex-1"
                />
              </Suspense>
            )}
          </Flex>
        </DialogContent>
      </Dialog>
    </Flex>
  );
}
